import { useEffect, useRef } from "react";
import { showConfirmationDialog } from "../../other/utils/utils.jsx";
import { updateURLQueryParam } from "../../other/utils/urlUtils.jsx";

const timeout = 10000; // 10 seconds
const TIMESTAMP_KEY = "timestamp";
const QUERY_PARAM_KEY = "_cb";
const versionUrl = "/timestamp.json";
const maxRetries = 3;
const checkInterval = 60000; // 1 minute

const changeFavicon = (url) => {
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

const useCacheBuster = (isAppLoaded) => {
  const isCheckingRef = useRef(false);
  const retryCountRef = useRef(0);
  const intervalRef = useRef(null);
  const currentTimeStampRef = useRef(localStorage.getItem(TIMESTAMP_KEY));
  const dismissedTimeStampRef = useRef(false);

  const clearCaches = async () => {
    try {
      console.info("[CacheBuster] Clearing caches...")
      // Clear service workers
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registrations.map(reg => reg.unregister()))
        console.info("[CacheBuster] Service workers cleared")
      }
      // Clear browser caches
      if ("caches" in window) {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map(name => caches.delete(name)))
      }
      console.info("[CacheBuster] Storage cleared")
    } catch (error) {
      console.error("[CacheBuster] Cache clearing failed:", error)
      throw error
    }
  }

  const performReload = async (newTimeStamp) => {
    try {
      changeFavicon("/swivu-grey.ico")
      const canReload = await showConfirmationDialog({
        title: "Swivu Update Available",
        message: "A new version of the application is available. Do you want to reload now?",
        cancelBtnText: "Close",
        confirmBtnText: "Reload"
      })
      if (canReload) {
        localStorage.setItem(TIMESTAMP_KEY, JSON.stringify(newTimeStamp));
        await clearCaches()
        // Hard reload to bypass all caches
        window.location.href = window.location.href.split("?")[0] + `?${QUERY_PARAM_KEY}=${newTimeStamp}`;
      } else {
        dismissedTimeStampRef.current = newTimeStamp
      }
    } catch (error) {
      console.error("[CacheBuster] Reload failed:", error)
    }
  }

  const checkVersion = async () => {
    if (isCheckingRef.current) return
    isCheckingRef.current = true
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)
      const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0"
        },
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      const data = await response.json()

      if (!data[TIMESTAMP_KEY]) {
        throw new Error("Invalid version response: missing version field")
      }
      retryCountRef.current = 0;

      const { [TIMESTAMP_KEY]: remoteVersion } = data
      let storedVersion = currentTimeStampRef.current;
      if (!storedVersion) {
        const latestTimeStamp = JSON.stringify(remoteVersion);
        localStorage.setItem(TIMESTAMP_KEY, latestTimeStamp);
        currentTimeStampRef.current = latestTimeStamp;
        return
      }
      storedVersion = JSON.parse(storedVersion);
      // Check for version mismatch
      const isNewVersion = remoteVersion !== (dismissedTimeStampRef.current || storedVersion);
      if (isNewVersion) {
        await performReload(remoteVersion);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      console.warn("[CacheBuster] Version check failed:", err.message)
      // Implement exponential backoff retry
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++
        const retryDelay = Math.min(5000 * Math.pow(2, retryCountRef.current - 1), checkInterval / 2)
        console.info(`[CacheBuster] Scheduling retry ${retryCountRef.current}/${maxRetries} in ${retryDelay}ms`)
        setTimeout(() => {
          isCheckingRef.current = false
          checkVersion()
        }, retryDelay)
      } else {
        console.warn("[CacheBuster] Max retries reached, will try again on next interval")
        retryCountRef.current = 0
      }
    } finally {
      // If retries mechanism is triggered then this condition will prevent extra calls from interval
      if (retryCountRef.current === 0) {
        isCheckingRef.current = false
      }
    }
  }

  useEffect(() => {
    if (isAppLoaded) {
      // If the app is loaded, we can safely remove the cache buster query param
      updateURLQueryParam(QUERY_PARAM_KEY, undefined, true);
    }
  }, [isAppLoaded]);

  useEffect(() => {
    checkVersion();
    intervalRef.current = setInterval(checkVersion, checkInterval)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      isCheckingRef.current = false
    }
  }, [])
}

export default useCacheBuster;